import { useEffect, useState } from "react";
import { Repeat, TrendingUp, TrendingDown, Loader2, Inbox } from "lucide-react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import RenewalInsights from "./RenewalInsights";

/**
 * Renewal Pipeline analytics card. Surfaces negotiation KPIs computed from
 * the lease_renewal_proposals + counter_history audit trail:
 *   - acceptance rate (vs. industry 71%)
 *   - final-vs-asking % (vs. industry 94%)
 *   - average close days (vs. industry 21d)
 *   - counter rate (vs. industry 38%)
 *   - average turns to close, median rent give-back %
 *
 * Backed by `GET /api/lease-renewal-proposals/analytics`.
 */
export default function RenewalPipelineCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get("/lease-renewal-proposals/analytics")
      .then((r) => { if (mounted) setData(r.data); })
      .catch((err) => {
        if (mounted) toast.error(formatApiError(err.response?.data?.detail) || "Could not load renewal analytics");
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6" data-testid="renewal-pipeline-card">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading renewal pipeline…
        </div>
      </div>
    );
  }

  if (!data) return null;

  const benchmarks = data.benchmarks || {};
  const counts = data.counts || {};
  const total = data.total || 0;

  if (total === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6" data-testid="renewal-pipeline-card">
        <div className="flex items-center gap-2 mb-3">
          <Repeat className="w-4 h-4 text-slate-700" />
          <h2 className="text-base font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>
            Renewal pipeline
          </h2>
        </div>
        <div className="flex items-center gap-3 text-sm text-slate-500" data-testid="renewal-pipeline-empty">
          <Inbox className="w-5 h-5 text-slate-400" />
          No renewal proposals yet — your negotiation analytics light up the moment you send your first offer.
          <Link to="/dashboard/leases" className="text-slate-900 font-semibold underline">Go to Leases →</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6" data-testid="renewal-pipeline-card">
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="overline text-slate-500">Negotiation analytics</div>
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
            Renewal pipeline
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            {counts.pending || 0} pending · {counts.countered || 0} countered · {counts.accepted || 0} accepted · {counts.declined || 0} declined
            {counts.expired ? ` · ${counts.expired} expired` : ""}
          </p>
        </div>
        <Link
          to="/dashboard/leases"
          className="text-xs font-semibold text-slate-700 hover:text-slate-900 underline"
          data-testid="renewal-pipeline-cta"
        >
          Open Leases →
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KpiTile
          label="Acceptance rate"
          value={data.acceptance_rate_pct}
          format="pct"
          benchmark={benchmarks.acceptance_rate}
          benchmarkLabel="industry"
          higherIsBetter
          testid="kpi-acceptance"
        />
        <KpiTile
          label="Final vs. asking"
          value={data.median_final_vs_asking_pct}
          format="pct"
          benchmark={benchmarks.final_vs_asking_pct}
          benchmarkLabel="industry"
          higherIsBetter
          testid="kpi-final-vs-asking"
        />
        <KpiTile
          label="Avg time to close"
          value={data.avg_close_days}
          format="days"
          benchmark={benchmarks.avg_close_days}
          benchmarkLabel="industry"
          higherIsBetter={false}
          testid="kpi-close-days"
        />
        <KpiTile
          label="Counter rate"
          value={data.counter_rate_pct}
          format="pct"
          benchmark={benchmarks.counter_rate}
          benchmarkLabel="industry"
          higherIsBetter={false}
          neutralAtParity
          testid="kpi-counter-rate"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4 border-t border-slate-100">
        <SecondaryStat
          label="Avg turns to close"
          value={data.avg_turns_to_close == null ? "—" : data.avg_turns_to_close.toFixed(1)}
          hint="A turn = one round-trip (offer + counter)."
          testid="stat-turns"
        />
        <SecondaryStat
          label="Median rent give-back"
          value={data.median_rent_giveback_pct == null ? "—" : `${data.median_rent_giveback_pct.toFixed(1)}%`}
          hint="Discount from initial asking rent (lower = stronger)."
          testid="stat-giveback"
        />
        <SecondaryStat
          label="Median close time"
          value={data.median_close_days == null ? "—" : `${data.median_close_days.toFixed(1)}d`}
          hint="Half of resolved proposals close within this window."
          testid="stat-median-days"
        />
      </div>

      <div className="mt-5 pt-4 border-t border-slate-100">
        <RenewalInsights showAll />
      </div>
    </div>
  );
}

function KpiTile({ label, value, format, benchmark, benchmarkLabel, higherIsBetter, neutralAtParity, testid }) {
  const fmt = (v) => {
    if (v == null) return "—";
    if (format === "pct") return `${Number(v).toFixed(1)}%`;
    if (format === "days") return `${Number(v).toFixed(1)}d`;
    return Number(v).toLocaleString();
  };
  let delta = null;
  let deltaTone = "text-slate-400";
  let DeltaIcon = TrendingUp;
  if (value != null && benchmark != null) {
    const diff = Number(value) - Number(benchmark);
    if (Math.abs(diff) < 0.05) {
      delta = "at par";
      deltaTone = "text-slate-500";
      DeltaIcon = TrendingUp;
    } else {
      const better = higherIsBetter ? diff > 0 : diff < 0;
      const sign = diff > 0 ? "+" : "";
      const absDiff = Math.abs(diff);
      delta = `${sign}${diff.toFixed(1)}${format === "pct" ? "pp" : format === "days" ? "d" : ""} vs. ${benchmarkLabel}`;
      if (neutralAtParity && absDiff < 5) {
        deltaTone = "text-slate-500";
      } else {
        deltaTone = better ? "text-emerald-700" : "text-rose-700";
      }
      DeltaIcon = better ? TrendingUp : TrendingDown;
    }
  }
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500" style={{ fontFamily: "Outfit" }}>{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-1 font-mono">{fmt(value)}</div>
      {delta ? (
        <div className={`text-[11px] font-mono mt-0.5 inline-flex items-center gap-1 ${deltaTone}`}>
          <DeltaIcon className="w-3 h-3" /> {delta}
        </div>
      ) : (
        <div className="text-[11px] text-slate-400 mt-0.5 font-mono">benchmark: {fmt(benchmark)}</div>
      )}
    </div>
  );
}

function SecondaryStat({ label, value, hint, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500" style={{ fontFamily: "Outfit" }}>{label}</div>
      <div className="text-lg font-bold text-slate-900 mt-0.5 font-mono">{value}</div>
      <div className="text-[11px] text-slate-400 mt-0.5">{hint}</div>
    </div>
  );
}
